/**
 * Schema.org Course JSON-LD for course-detail pages.
 *
 * <p>Google retired Course SERP cards Jan 2026, but AI search engines
 * (Bing/ChatGPT/Claude/Perplexity) still read this as the primary
 * "what is this page" signal, and {@code AggregateRating} still
 * produces star eligibility under organic results.
 *
 * <p>Host-agnostic: the caller passes plain fields ({@link CourseSchemaInput})
 * rather than a host-specific entity type.
 */
import { JsonLdScript } from './_shared';

export interface CourseSchemaInput {
  title: string;
  description?: string;
  tagline?: string;
  summary?: string;
  imageUrl?: string;
  language?: string;
  lastUpdated?: string;
  level?: string;
  outcomes?: string[];
  requirements?: string[];
  targetAudience?: string[];
  ratingAverage?: number;
  ratingCount?: number;
  enrolled?: number;
  duration?: string;
  totalHours?: number;
  /** Free/cohort/etc. — present means an instance is emitted. */
  format?: string;
  isFree?: boolean;
  price?: number;
  priceCurrency?: string;
  instructor?: {
    name?: string;
    role?: string;
    avatar?: string;
  };
}

export interface CourseSchemaJsonLdProps {
  course: CourseSchemaInput;
  /** Tenant slug — used as Organization fallback. */
  tenantSlug: string;
  /** Display name override. */
  providerName?: string;
  /** Canonical absolute URL of this detail page. */
  canonicalUrl: string;
  providerLogoUrl?: string;
  id?: string;
}

export function CourseSchemaJsonLd({
  course,
  tenantSlug,
  providerName,
  canonicalUrl,
  providerLogoUrl,
  id,
}: CourseSchemaJsonLdProps) {
  const schema = buildCourseSchema({
    course,
    tenantSlug,
    providerName: providerName ?? tenantSlug,
    canonicalUrl,
    providerLogoUrl,
  });
  return <JsonLdScript schema={schema} id={id ?? 'schema-course'} />;
}

export function buildCourseSchema(args: {
  course: CourseSchemaInput;
  tenantSlug: string;
  providerName: string;
  canonicalUrl: string;
  providerLogoUrl?: string;
}) {
  const { course, providerName, canonicalUrl, providerLogoUrl } = args;
  const instructor = instructorToPerson(course.instructor);
  const rating = ratingToAggregate(course);
  const offers = courseToOffers(course);
  const instances = course.format ? [buildInstance(course, offers)] : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.description ?? course.tagline ?? course.summary ?? course.title,
    url: canonicalUrl,
    image: course.imageUrl,
    inLanguage: course.language,
    dateModified: course.lastUpdated,
    educationalLevel: course.level,
    teaches: course.outcomes,
    coursePrerequisites: course.requirements,
    audience: course.targetAudience
      ? {
          '@type': 'EducationalAudience',
          audienceType: course.targetAudience.join(', '),
        }
      : undefined,
    provider: {
      '@type': 'Organization',
      name: providerName,
      logo: providerLogoUrl,
    },
    author: instructor,
    aggregateRating: rating,
    hasCourseInstance: instances,
    offers,
  };
}

function instructorToPerson(instructor: CourseSchemaInput['instructor']) {
  if (!instructor || !instructor.name) return undefined;
  return {
    '@type': 'Person',
    name: instructor.name,
    jobTitle: instructor.role,
    image: instructor.avatar,
  };
}

function ratingToAggregate(course: CourseSchemaInput) {
  if (!course.ratingAverage || typeof course.ratingAverage !== 'number') return undefined;
  return {
    '@type': 'AggregateRating',
    ratingValue: course.ratingAverage,
    ratingCount: course.ratingCount ?? course.enrolled ?? 1,
    bestRating: 5,
    worstRating: 1,
  };
}

function courseToOffers(course: CourseSchemaInput) {
  if (course.isFree) {
    return {
      '@type': 'Offer',
      price: 0,
      priceCurrency: course.priceCurrency ?? 'USD',
      availability: 'https://schema.org/InStock',
      category: 'Free',
    };
  }
  if (typeof course.price === 'number' && course.price > 0) {
    return {
      '@type': 'Offer',
      price: course.price,
      priceCurrency: course.priceCurrency ?? 'USD',
      availability: 'https://schema.org/InStock',
    };
  }
  return undefined;
}

function buildInstance(course: CourseSchemaInput, offers: ReturnType<typeof courseToOffers>) {
  return {
    '@type': 'CourseInstance',
    courseMode: 'Online',
    courseWorkload: course.duration ?? totalHoursToISO(course.totalHours),
    offers,
  };
}

function totalHoursToISO(hours: number | undefined): string | undefined {
  if (typeof hours !== 'number' || !Number.isFinite(hours) || hours <= 0) return undefined;
  return `PT${Math.round(hours)}H`;
}
